<script lang="ts">
    import { onMount } from "svelte";
    import { apiClient } from "./apiClient";
    import { categories, loading, error, success } from "./stores";
    import type { Category } from "./apiClient";

    let showForm = false;
    let newCategoryName = "";
    let editingCategoryId: number | null = null;

    onMount(async () => {
        await loadCategories();
    });

    async function loadCategories() {
        try {
            loading.set(true);
            error.set(null);

            const categoriesData = await apiClient.getCategories();
            categories.set(categoriesData);
        } catch (err) {
            error.set(
                err instanceof Error
                    ? err.message
                    : "Failed to load categories",
            );
        } finally {
            loading.set(false);
        }
    }

    async function addCategory() {
        if (!newCategoryName.trim()) {
            error.set("Category name is required");
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            await apiClient.createCategory({ name: newCategoryName.trim() });
            newCategoryName = "";
            showForm = false;
            await loadCategories();
            success.set("Category added successfully");
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to add category",
            );
        } finally {
            loading.set(false);
        }
    }

    async function deleteCategory(category: Category) {
        if (!confirm(`Are you sure you want to delete "${category.name}"?`)) {
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            await apiClient.deleteCategory(category.id);
            await loadCategories();
            success.set("Category deleted successfully");
        } catch (err) {
            error.set(
                err instanceof Error
                    ? err.message
                    : "Failed to delete category",
            );
        } finally {
            loading.set(false);
        }
    }

    function toggleForm() {
        showForm = !showForm;
        if (!showForm) {
            newCategoryName = "";
            editingCategoryId = null;
        } else {
            editingCategoryId = -1; // Special ID for new category
        }
    }
</script>

<div class="section">
    <h2>Categories Management</h2>

    <button class="btn btn-primary" on:click={toggleForm}>
        {showForm ? "Cancel" : "Add New Category"}
    </button>

    {#if showForm && editingCategoryId === -1}
        <div class="inline-form">
            <div class="form-group">
                <label for="category-name">Category Name</label>
                <input
                    id="category-name"
                    type="text"
                    bind:value={newCategoryName}
                    placeholder="Enter category name"
                    on:keydown={(e) => e.key === "Enter" && addCategory()}
                />
                <button
                    class="btn btn-success"
                    on:click={addCategory}
                    disabled={$loading}
                >
                    {$loading ? "Adding..." : "Add Category"}
                </button>
            </div>
        </div>
    {/if}

    {#if $loading}
        <div class="loading">Loading...</div>
    {/if}

    {#if $categories.length > 0}
        <table class="table">
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {#each $categories as category}
                    <tr>
                        <td data-label="ID">{category.id}</td>
                        <td data-label="Name">{category.name}</td>
                        <td data-label="Actions" class="actions">
                            <button
                                class="btn btn-danger"
                                on:click={() => deleteCategory(category)}
                                disabled={$loading}
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    {:else if !$loading}
        <p>No categories found. Add your first category!</p>
    {/if}
</div>

<style>
    .inline-form {
        padding: 20px;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        background-color: #fff;
        margin: 10px 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
</style>
